import { useEffect, useRef, useState } from 'react';
import './FundCriteriaSetting.css';
import '../SingleSelectCriterion/SingleSelectCriterion.css';
import '../../../Common/typography.css';
import {
  FundCriteria,
  FundCriteriaSetter,
} from '../../../../types/fundScreener.type';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-line.svg';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrow-down-s-line.svg';
import RangeSlider from '../../../Common/RangeSlider/RangeSlider';
import { Select, MenuItem } from '@mui/material';
import { useFundCriterionMinMax } from '../../../../hooks/FundScreener/useFundCriterionMinMax';
import { useFundCompanySearch } from '../../../../hooks/FundScreener/useFundCompanySearch';
import {
  FUND_CURRENCY_OPTIONS,
  DEFAULT_FUND_CURRENCY,
  DEFAULT_SCORE_VALUES,
} from '../../../../constants/fundScreener/fundCriteria.constant';
import { formatLocaleNumber } from '../../../../helpers/number';
import {
  logValue2TrueValue,
  truevalue2logvalue,
} from '../../../../utils/utils';

// Same convention as the results table's NAV column.
const getCriterionDecimals = (criterion: FundCriteria, market: string) => {
  if (criterion.id === 'nav') return market === 'AU' ? 4 : 2;
  return 2;
};

export interface FundCriteriaSettingProps {
  market: string;
  tempCriteria: FundCriteria[];
  criteria: FundCriteria;
  setCriteria: FundCriteriaSetter;
  token: string;
}

const SCORE_VALUES = [5, 4, 3, 2, 1];

const updateCriteria = (
  list: FundCriteria[],
  id: string,
  patch: Partial<FundCriteria>,
): FundCriteria[] =>
  list.map((item) =>
    item.id === id ? { ...item, ...patch, isInitial: false } : item,
  );

const FundCriteriaSetting = ({
  market,
  tempCriteria,
  criteria,
  setCriteria,
  token,
}: FundCriteriaSettingProps) => {
  const [timeValue, setTimeValue] = useState(
    criteria.timeValue || criteria.timeOptions?.[0] || '',
  );
  const [tempLower, setTempLower] = useState(
    criteria.selectedMin?.toString() ?? '',
  );
  const [tempUpper, setTempUpper] = useState(
    criteria.selectedMax?.toString() ?? '',
  );
  // Raw number while focused, formatted (rounded + thousands-separated) otherwise.
  const [isLowerFocused, setIsLowerFocused] = useState(false);
  const [isUpperFocused, setIsUpperFocused] = useState(false);
  const decimals = getCriterionDecimals(criteria, market);
  const displayLower = isLowerFocused
    ? tempLower
    : formatLocaleNumber(
        tempLower === '' ? null : parseFloat(tempLower),
        decimals,
      );
  const displayUpper = isUpperFocused
    ? tempUpper
    : formatLocaleNumber(
        tempUpper === '' ? null : parseFloat(tempUpper),
        decimals,
      );
  const [companyQuery, setCompanyQuery] = useState(criteria.companyName ?? '');
  const {
    options: companyOptions,
    search: searchCompany,
    openWithCurrentKeyword: openCompanySearch,
    clear: clearCompanyOptions,
  } = useFundCompanySearch(token, market);

  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const currencyDropdownRef = useRef<HTMLDivElement>(null);
  const companyDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        currencyDropdownRef.current &&
        !currencyDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCurrencyOpen(false);
      }
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(event.target as Node)
      ) {
        clearCompanyOptions();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clearCompanyOptions]);

  // AUD is preselected as soon as the Fund currency criterion is added.
  useEffect(() => {
    if (criteria.controlType === 'currency' && !criteria.currency) {
      setCriteria((prev) =>
        updateCriteria(prev, criteria.id, { currency: DEFAULT_FUND_CURRENCY }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria.id]);

  // FundScreenerWidget clears `criteria.companyName` synchronously on market
  // change (a management company belongs to one market) — this just keeps
  // the local input/dropdown UI in sync with that.
  useEffect(() => {
    if (
      criteria.id === 'fundCompany' &&
      !criteria.companyName &&
      companyQuery
    ) {
      setCompanyQuery('');
      clearCompanyOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria.companyName]);

  useFundCriterionMinMax({
    market,
    token,
    criterion: { ...criteria, timeValue },
    tempCriteria,
    // Functional updater so the two same-tick calls below don't patch off a
    // stale snapshot and wipe out each other's update.
    onPendingChange: (pending) =>
      setCriteria((prev) =>
        updateCriteria(prev, criteria.id, { pendingMinMax: pending }),
      ),
    onResult: ({ min, max, marketChanged }) => {
      // Only preserve the old sub-range for the SAME period on the SAME
      // market; either changing must reset to the new min/max (different scale).
      const periodUnchanged =
        criteria.timeValue === timeValue && !marketChanged;
      const selectedMin = periodUnchanged ? (criteria.selectedMin ?? min) : min;
      const selectedMax = periodUnchanged ? (criteria.selectedMax ?? max) : max;
      setTempLower(selectedMin.toString());
      setTempUpper(selectedMax.toString());
      setCriteria((prev) =>
        updateCriteria(prev, criteria.id, {
          min,
          max,
          timeValue,
          selectedMin,
          selectedMax,
          minMaxUnavailable: false,
          minMaxMarket: market,
        }),
      );
    },
    onUnavailable: () =>
      setCriteria((prev) =>
        updateCriteria(prev, criteria.id, { minMaxUnavailable: true }),
      ),
  });

  // Log scale anchored at `min` (no BE distribution mid) — keeps ranges
  // draggable at any span, including negative ones.
  const truemid = criteria.min;
  const logMin = truevalue2logvalue(criteria.min, truemid);
  const logMax = truevalue2logvalue(criteria.max, truemid);
  const scaleStep = (logMax - logMin) / 100 || 1;
  const tscaleLower = truevalue2logvalue(
    criteria.selectedMin ?? criteria.min,
    truemid,
  );
  const tscaleUpper = truevalue2logvalue(
    criteria.selectedMax ?? criteria.max,
    truemid,
  );

  const handleRangeSliderChange = (_: Event, value: number | number[]) => {
    if (!Array.isArray(value)) return;
    // Full precision here — `decimals` is display-only rounding; truncating
    // the stored value could wrongly exclude funds at the boundary.
    const newMin = logValue2TrueValue(value[0], truemid);
    const newMax = logValue2TrueValue(value[1], truemid);
    setTempLower(newMin.toString());
    setTempUpper(newMax.toString());
    setCriteria((prev) =>
      updateCriteria(prev, criteria.id, {
        selectedMin: newMin,
        selectedMax: newMax,
      }),
    );
  };

  const commitInput = (type: 'LOWER' | 'UPPER', raw: string) => {
    const parsed = parseFloat(raw.replace(/,/g, ''));
    if (isNaN(parsed)) return;
    const clamped = Math.min(Math.max(parsed, criteria.min), criteria.max);
    if (type === 'LOWER') {
      const newMin = Math.min(clamped, criteria.selectedMax ?? criteria.max);
      setTempLower(newMin.toString());
      setCriteria((prev) =>
        updateCriteria(prev, criteria.id, { selectedMin: newMin }),
      );
    } else {
      const newMax = Math.max(clamped, criteria.selectedMin ?? criteria.min);
      setTempUpper(newMax.toString());
      setCriteria((prev) =>
        updateCriteria(prev, criteria.id, { selectedMax: newMax }),
      );
    }
  };

  // Each score chip is an independent toggle, not a continuous range — the
  // BE payload is `scores: [3, 4, 5]`, unselected ones just absent.
  const effectiveScores = criteria.scores ?? DEFAULT_SCORE_VALUES;

  const toggleScore = (score: number | 'NA') => {
    const next = effectiveScores.includes(score)
      ? effectiveScores.filter((s) => s !== score)
      : [...effectiveScores, score];
    setCriteria((prev) => updateCriteria(prev, criteria.id, { scores: next }));
  };

  const handleCompanyInput = (value: string) => {
    setCompanyQuery(value);
    searchCompany(value);
    // When the user clears the input, also clear the persisted company name so
    // the next Get Results call doesn't still filter by the old selection.
    if (!value && criteria.companyName) {
      setCriteria((prev) =>
        updateCriteria(prev, criteria.id, { companyName: undefined }),
      );
    }
  };

  const selectCompany = (option: { value: string; label: string }) => {
    setCompanyQuery(option.label);
    clearCompanyOptions();
    setCriteria((prev) =>
      updateCriteria(prev, criteria.id, { companyName: option.label }),
    );
  };

  const selectCurrency = (currency: string) => {
    setCriteria((prev) => updateCriteria(prev, criteria.id, { currency }));
    setIsCurrencyOpen(false);
  };

  const handleRemove = () => {
    setCriteria((prev) =>
      prev.map((item) =>
        item.id === criteria.id ? { ...item, selected: false } : item,
      ),
    );
  };

  return (
    <div className="fund-criteria-setting">
      <div className="fund-criteria-setting-header">
        <div className="fund-criteria-setting-header-title header-6">
          {criteria.shortLabel || criteria.label}
        </div>
        <CloseIcon
          className="fund-criteria-setting-header-close"
          onClick={handleRemove}
        />
      </div>

      {criteria.timeOptions && criteria.timeOptions.length > 0 && (
        <Select
          className="fund-criteria-setting-time-select"
          variant="standard"
          disableUnderline
          sx={{
            width: '120px',
            height: '40px',
            borderRadius: '4px',
            border: '1px solid var(--border-primary)',
            fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 400,
            color: 'var(--text-input-2)',
            '& .MuiSelect-select': {
              height: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              padding: '8px 8px 8px 16px',
            },
            '& .MuiSelect-icon': {
              width: '16px',
              height: '16px',
              right: '4px',
            },
            '&:hover': {
              border: '1px solid var(--border-primary-brand)',
            },
          }}
          value={timeValue}
          onChange={(e) => {
            const nextTimeValue = e.target.value;
            setTimeValue(nextTimeValue);
            // Range criteria must not write timeValue here (defeats the
            // periodUnchanged check above); score criteria skip that fetch entirely.
            if (criteria.controlType === 'score') {
              setCriteria((prev) =>
                updateCriteria(prev, criteria.id, { timeValue: nextTimeValue }),
              );
            }
          }}
          IconComponent={ArrowDownIcon}
        >
          {criteria.timeOptions.map((opt) => (
            <MenuItem
              key={opt}
              value={opt}
              sx={{
                padding: '4px 8px',
                fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
                fontSize: '16px',
                lineHeight: '24px',
                fontWeight: 400,
                color: 'var(--text-input-2)',
                '&:hover': {
                  backgroundColor: 'var(--color-brand-hover2)',
                },
                '&.Mui-selected': {
                  backgroundColor: 'var(--color-brand-hover2)',
                  '&:hover': {
                    backgroundColor: 'var(--color-brand-hover2)',
                  },
                },
              }}
            >
              {opt === 'Overall' ? opt : opt.toUpperCase()}
            </MenuItem>
          ))}
        </Select>
      )}

      {criteria.controlType === 'range' && (
        <>
          {criteria.minMaxUnavailable ? (
            <div className="fund-criteria-setting-no-data body-s-regular">
              No results found for this criteria
            </div>
          ) : (
            <>
              <div className="fund-criteria-setting-slider-container">
                <RangeSlider
                  min={logMin}
                  max={logMax}
                  step={scaleStep}
                  value={[tscaleLower, tscaleUpper]}
                  handleChange={handleRangeSliderChange}
                />
              </div>
              <div className="fund-criteria-setting-input-container">
                <input
                  className="fund-criteria-setting-input body-m-regular"
                  type="text"
                  inputMode="decimal"
                  value={displayLower}
                  onFocus={() => {
                    setTempLower(displayLower);
                    setIsLowerFocused(true);
                  }}
                  onChange={(e) => setTempLower(e.target.value)}
                  onBlur={(e) => {
                    commitInput('LOWER', e.target.value);
                    setIsLowerFocused(false);
                  }}
                />
                <div className="fund-criteria-setting-input-label body-s-regular">
                  TO
                </div>
                <input
                  className="fund-criteria-setting-input body-m-regular"
                  type="text"
                  inputMode="decimal"
                  value={displayUpper}
                  onFocus={() => {
                    setTempUpper(displayUpper);
                    setIsUpperFocused(true);
                  }}
                  onChange={(e) => setTempUpper(e.target.value)}
                  onBlur={(e) => {
                    commitInput('UPPER', e.target.value);
                    setIsUpperFocused(false);
                  }}
                />
              </div>
            </>
          )}
        </>
      )}

      {criteria.controlType === 'score' && (
        <div className="fund-criteria-setting-score-chips">
          {SCORE_VALUES.map((score) => {
            const enabled = effectiveScores.includes(score);
            // Only "5" ever renders solid; other enabled scores stay outlined.
            const isTop = enabled && score === 5;
            return (
              <button
                key={score}
                type="button"
                className={`fund-criteria-setting-score-chip ${isTop ? 'solid' : enabled ? 'outlined' : ''}`}
                onClick={() => toggleScore(score)}
              >
                {score}
              </button>
            );
          })}
          <button
            type="button"
            className={`fund-criteria-setting-score-chip na ${effectiveScores.includes('NA') ? 'outlined' : ''}`}
            onClick={() => toggleScore('NA')}
          >
            NA
          </button>
        </div>
      )}

      {criteria.controlType === 'search' && (
        <div ref={companyDropdownRef} className="fund-criteria-setting-search">
          <input
            className="fund-criteria-setting-search-input body-s-regular"
            type="text"
            placeholder="Search Name"
            value={companyQuery}
            // Clicking into the input should show a result list right away,
            // like a normal search dropdown, not wait for the user to type.
            onFocus={() => openCompanySearch(companyQuery)}
            onChange={(e) => handleCompanyInput(e.target.value)}
          />
          {companyOptions.length > 0 && (
            <div className="fund-criteria-setting-search-dropdown">
              {companyOptions.map((option) => (
                <div
                  key={option.value}
                  className="fund-criteria-setting-search-option body-s-regular"
                  onClick={() => selectCompany(option)}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {criteria.controlType === 'currency' && (
        <div ref={currencyDropdownRef} className="fund-single-select-criterion">
          <button
            type="button"
            className="fund-single-select-button body-m-regular"
            aria-haspopup="listbox"
            aria-expanded={isCurrencyOpen}
            onClick={() => setIsCurrencyOpen((prev) => !prev)}
          >
            <span className="fund-single-select-button-label">
              {criteria.currency ?? DEFAULT_FUND_CURRENCY}
            </span>
            <ArrowDownIcon
              className={`fund-single-select-expand ${isCurrencyOpen ? 'open' : 'closed'}`}
            />
          </button>

          {isCurrencyOpen && (
            <div className="fund-single-select-dropdown">
              <div className="fund-single-select-options">
                {FUND_CURRENCY_OPTIONS.map((option) => (
                  <div
                    key={option}
                    className={`fund-single-select-option-item body-m-regular ${
                      (criteria.currency ?? DEFAULT_FUND_CURRENCY) === option
                        ? 'active'
                        : ''
                    }`}
                    role="option"
                    aria-selected={
                      (criteria.currency ?? DEFAULT_FUND_CURRENCY) === option
                    }
                    onClick={() => selectCurrency(option)}
                  >
                    {option}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FundCriteriaSetting;
